import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Send, Sparkles, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import MarkdownContent from '../chat/MarkdownContent';

const API = process.env.REACT_APP_BACKEND_URL;

const QUICK_EDITS = [
  '第 3 頁顏色感覺太搶眼，調淡一點',
  '封面標題再放大字級、留白多一點',
  '幫第 2 頁生成一張霧夜森林的背景圖',
  '最後加多一頁行動呼籲頁',
  '刪除第 4 頁',
  '將第 3 頁移到第 1 頁之前',
  '將最後一頁的文字改成更輕鬆的口吻',
];

/**
 * Pages Chat Panel — Screen 3 右側對話欄（Commit 3）
 *
 * Flow:
 *  1. Load chat history on mount via GET /api/carousel/{id}/chat
 *  2. On submit: POST /api/carousel/{id}/chat/stream (SSE)
 *     - token: stream assistant conversational reply
 *     - edit_start: show "編輯中 pX..." indicator
 *     - page_edit: parent callback to refresh iframe of that page
 *     - done: persist, show edit badges
 */
export const CarouselPagesChat = ({ carouselId, pages, onPageEdit, onPageAdded, onPageDeleted, onPageReordered, onRefreshUser, onAddCredits, pendingPrompt }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [creditsUsed, setCreditsUsed] = useState(0);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const scrollRef = useRef(null);
  const streamingIdxRef = useRef(null);

  // Load chat history on mount
  useEffect(() => {
    if (!carouselId || historyLoaded) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/carousel/${carouselId}/chat`);
        const msgs = (r.data?.messages || []).map((m) => ({
          role: m.role,
          content: m.content,
          edits: m.edits || [],
        }));
        setMessages(msgs);
      } catch (_) { /* first-time empty */ }
      setHistoryLoaded(true);
    })();
  }, [carouselId, historyLoaded]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Ref to the textarea so we can focus it when a pending prompt is injected
  const inputElRef = useRef(null);

  // When parent injects a pendingPrompt (inline page action clicked), load it into
  // the input as a DRAFT — user must still click Send. This prevents accidental
  // double-sends if the user misclicks an action icon.
  const lastPendingTsRef = useRef(null);
  useEffect(() => {
    if (!pendingPrompt || !pendingPrompt.text) return;
    if (lastPendingTsRef.current === pendingPrompt.ts) return;
    lastPendingTsRef.current = pendingPrompt.ts;
    setInput(pendingPrompt.text);
    // Focus the textarea and move cursor to end so user can edit or hit Enter
    requestAnimationFrame(() => {
      if (inputElRef.current) {
        inputElRef.current.focus();
        const len = pendingPrompt.text.length;
        try {
          inputElRef.current.setSelectionRange(len, len);
        } catch (_) {
          /* ignore browsers that don't support it */
        }
      }
    });
  }, [pendingPrompt]);

  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || sending) return;
    setInput('');
    setSending(true);

    setMessages((prev) => {
      const next = [
        ...prev,
        { role: 'user', content: text },
        { role: 'assistant', content: '', streaming: true, edits: [] },
      ];
      streamingIdxRef.current = next.length - 1;
      return next;
    });

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/carousel/${carouselId}/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message: text }),
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          detail = j.detail || detail;
        } catch (_) { /* ignore */ }
        throw new Error(detail);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let sseBuf = '';
      const editedIndexes = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        sseBuf += decoder.decode(value, { stream: true });
        const parts = sseBuf.split('\n\n');
        sseBuf = parts.pop() || '';
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith('data:')) continue;
          const payload = line.slice(5).trim();
          if (!payload) continue;
          let evt;
          try {
            evt = JSON.parse(payload);
          } catch (_) { continue; }
          if (evt.type === 'token' && evt.text) {
            setMessages((prev) => {
              const next = [...prev];
              const idx = streamingIdxRef.current;
              if (next[idx]) next[idx] = { ...next[idx], content: next[idx].content + evt.text };
              return next;
            });
          } else if (evt.type === 'edit_start') {
            // Optional UI hint — add a marker line to current assistant bubble
            setMessages((prev) => {
              const next = [...prev];
              const idx = streamingIdxRef.current;
              if (next[idx]) {
                next[idx] = {
                  ...next[idx],
                  content:
                    (next[idx].content || '').trimEnd() +
                    `\n\n〔正在重寫 p${evt.index}…〕`,
                };
              }
              return next;
            });
          } else if (evt.type === 'image_start') {
            setMessages((prev) => {
              const next = [...prev];
              const idx = streamingIdxRef.current;
              if (next[idx]) {
                next[idx] = {
                  ...next[idx],
                  content:
                    (next[idx].content || '').trimEnd() +
                    `\n\n🎨 正在為第 ${evt.page} 頁生成背景圖…`,
                };
              }
              return next;
            });
          } else if (evt.type === 'image_failed') {
            setMessages((prev) => {
              const next = [...prev];
              const idx = streamingIdxRef.current;
              if (next[idx]) {
                const reasonMap = {
                  budget_exceeded: '圖片服務額度已用完，請管理員加值 Emergent LLM Key',
                  quota_exceeded: '圖片 API 配額已達上限',
                  rate_limited: '請求太頻繁，請稍後再試',
                  no_images: 'AI 返回空白（prompt 可能被過濾）',
                  empty_data: 'AI 回傳空圖像',
                  exception: '系統錯誤',
                  unknown: '未知原因',
                };
                const why = reasonMap[evt.reason] || `未知原因（${evt.reason}）`;
                next[idx] = {
                  ...next[idx],
                  content:
                    (next[idx].content || '').trimEnd() +
                    `\n\n⚠️ p${evt.page} 圖片生成失敗：${why}（保留原背景）`,
                };
              }
              return next;
            });
            // Toast for immediate visibility
            if (evt.reason === 'budget_exceeded') {
              toast.error('Emergent LLM Key 額度已滿，請至 Profile → Universal Key 加值');
            } else {
              toast.error(`p${evt.page} 圖片生成失敗`);
            }
          } else if (evt.type === 'page_edit' && typeof evt.index === 'number' && typeof evt.html === 'string') {
            editedIndexes.push(evt.index);
            // evt.bg_image is populated by the backend for pages using the
            // decoupled-image architecture; forward it so parent can keep the
            // clean `html` and the `bg_image` cleanly separated in state.
            if (onPageEdit) onPageEdit(evt.index, evt.html, evt.bg_image);
            setMessages((prev) => {
              const next = [...prev];
              const idx = streamingIdxRef.current;
              if (next[idx]) {
                next[idx] = {
                  ...next[idx],
                  edits: [...(next[idx].edits || []), evt.index],
                };
              }
              return next;
            });
          } else if (evt.type === 'add_start') {
            setMessages((prev) => {
              const next = [...prev];
              const idx = streamingIdxRef.current;
              if (next[idx]) {
                next[idx] = {
                  ...next[idx],
                  content:
                    (next[idx].content || '').trimEnd() +
                    `\n\n➕ 新增一頁（在 p${evt.after} 之後）…`,
                };
              }
              return next;
            });
          } else if (evt.type === 'page_added' && typeof evt.new_index === 'number') {
            editedIndexes.push(evt.new_index);
            if (onPageAdded) onPageAdded({ index: evt.new_index, html: evt.html }, evt.after);
            setMessages((prev) => {
              const next = [...prev];
              const idx = streamingIdxRef.current;
              if (next[idx]) {
                next[idx] = {
                  ...next[idx],
                  edits: [...(next[idx].edits || []), evt.new_index],
                  content:
                    (next[idx].content || '').trimEnd() +
                    `\n\n✓ 已新增 p${evt.new_index}`,
                };
              }
              return next;
            });
          } else if (evt.type === 'page_deleted' && typeof evt.index === 'number') {
            if (onPageDeleted) onPageDeleted(evt.index);
            setMessages((prev) => {
              const next = [...prev];
              const idx = streamingIdxRef.current;
              if (next[idx]) {
                next[idx] = {
                  ...next[idx],
                  content:
                    (next[idx].content || '').trimEnd() +
                    `\n\n🗑️ 已刪除 p${evt.index}`,
                };
              }
              return next;
            });
          } else if (evt.type === 'delete_failed') {
            setMessages((prev) => {
              const next = [...prev];
              const idx = streamingIdxRef.current;
              if (next[idx]) {
                const why =
                  evt.reason === 'only_one_page'
                    ? '只剩一頁，不可再刪'
                    : evt.reason === 'not_found'
                    ? '找不到這一頁'
                    : '操作失敗';
                next[idx] = {
                  ...next[idx],
                  content:
                    (next[idx].content || '').trimEnd() +
                    `\n\n⚠️ 刪除第 ${evt.index} 頁失敗：${why}`,
                };
              }
              return next;
            });
          } else if (evt.type === 'page_reordered' && Array.isArray(evt.pages)) {
            if (onPageReordered) onPageReordered(evt.pages);
            setMessages((prev) => {
              const next = [...prev];
              const idx = streamingIdxRef.current;
              if (next[idx]) {
                next[idx] = {
                  ...next[idx],
                  content:
                    (next[idx].content || '').trimEnd() +
                    `\n\n↕️ 已重新排序 ${evt.pages.length} 頁`,
                };
              }
              return next;
            });
          } else if (evt.type === 'reorder_failed') {
            setMessages((prev) => {
              const next = [...prev];
              const idx = streamingIdxRef.current;
              if (next[idx]) {
                next[idx] = {
                  ...next[idx],
                  content:
                    (next[idx].content || '').trimEnd() +
                    `\n\n⚠️ 重排順序失敗（${evt.reason || '未知'}）`,
                };
              }
              return next;
            });
          } else if (evt.type === 'add_failed') {
            setMessages((prev) => {
              const next = [...prev];
              const idx = streamingIdxRef.current;
              if (next[idx]) {
                next[idx] = {
                  ...next[idx],
                  content:
                    (next[idx].content || '').trimEnd() +
                    `\n\n⚠️ 新增頁失敗`,
                };
              }
              return next;
            });
          } else if (evt.type === 'done') {
            setMessages((prev) => {
              const next = [...prev];
              const idx = streamingIdxRef.current;
              if (next[idx]) {
                next[idx] = { ...next[idx], streaming: false };
              }
              return next;
            });
            setCreditsUsed((c) => c + (evt.credits_charged || 0));
            // Always toast credits, mention edit count if any
            const credits = evt.credits_charged || 0;
            if (credits > 0 && onAddCredits) onAddCredits(credits);
            if (credits > 0) {
              const editSuffix = editedIndexes.length > 0 ? ` · 已更新 ${editedIndexes.length} 頁` : '';
              toast.success(`對話消耗 ${credits.toFixed(2)} 點數${editSuffix}`);
            } else if (editedIndexes.length > 0) {
              toast.success(`已更新 ${editedIndexes.length} 頁`);
            }
            if (onRefreshUser) onRefreshUser();
          } else if (evt.type === 'error') {
            throw new Error(evt.message || 'chat stream error');
          }
        }
      }
    } catch (e) {
      setMessages((prev) => {
        const next = [...prev];
        const idx = streamingIdxRef.current;
        if (next[idx]) {
          next[idx] = {
            ...next[idx],
            content: (next[idx].content || '') + `\n\n⚠️ ${e.message || '未知錯誤'}`,
            streaming: false,
            error: true,
          };
        }
        return next;
      });
      toast.error(`對話失敗：${e.message}`);
    } finally {
      setSending(false);
      streamingIdxRef.current = null;
    }
  };

  return (
    <div
      data-testid="carousel-pages-chat"
      className="flex flex-col h-[calc(100vh-13rem)] bg-atelier-surface border border-atelier-line rounded-2xl overflow-hidden atelier-card"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-atelier-line bg-atelier-surface2">
        <div className="flex items-center gap-2">
          <Wand2 className="w-4 h-4 text-atelier-coral" strokeWidth={1.6} />
          <span className="text-sm font-medium text-atelier-ink tracking-tight" style={{ fontFamily: "'Noto Serif TC', Georgia, serif" }}>對話編輯</span>
        </div>
        {creditsUsed > 0 && (
          <span
            data-testid="carousel-pages-chat-credits"
            className="atelier-caption px-2 py-0.5 rounded-full border border-atelier-coral/40 bg-atelier-coral/8 text-atelier-coral"
          >
            {creditsUsed.toFixed(2)} 點數
          </span>
        )}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 scroll-smooth">
        {messages.length === 0 && historyLoaded && (
          <div className="text-[12.5px] text-atelier-mute leading-[1.85]">
            <div className="flex items-start gap-2 mb-3">
              <Sparkles className="w-3.5 h-3.5 text-atelier-coral mt-0.5 flex-shrink-0" strokeWidth={1.6} />
              <span>
                想調整某頁？直接跟 AI 說，它會保持整組的節奏及風格，只修改你指定的頁面。
                修改後左側預覽會即時同步更新。
              </span>
            </div>
            <div className="atelier-caption mb-2">
              快速指令
            </div>
            <div className="space-y-1.5">
              {QUICK_EDITS.map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid={`carousel-pages-chat-quick-${s}`}
                  onClick={() => send(s)}
                  disabled={sending || !pages?.length}
                  className="w-full text-left text-[12px] text-atelier-mute hover:text-atelier-coral bg-atelier-surface hover:bg-atelier-coral/[0.06] border border-atelier-line hover:border-atelier-coral/50 rounded-lg px-3 py-2 transition-all duration-300 disabled:opacity-40"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        <AnimatePresence initial={false}>
          {messages.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              data-testid={`carousel-pages-chat-msg-${m.role}-${i}`}
            >
              <div
                className={`max-w-[90%] text-[12.5px] leading-relaxed rounded-lg px-3 py-2 ${
                  m.role === 'user'
                    ? 'bg-atelier-coral/[0.08] border border-atelier-coral/40 text-atelier-ink whitespace-pre-wrap'
                    : m.error
                      ? 'bg-red-500/10 border border-red-500/30 text-red-200 whitespace-pre-wrap'
                      : 'bg-atelier-surface border border-atelier-line text-atelier-ink'
                }`}
              >
                {m.content
                  ? (m.role === 'assistant' && !m.error
                      ? (
                          <div className="carousel-chat-md">
                            <MarkdownContent content={m.content} />
                          </div>
                        )
                      : m.content)
                  : (m.streaming && (
                      <span className="inline-flex items-center gap-1 text-atelier-mute text-[11px]">
                        <Loader2 className="w-3 h-3 animate-spin" /> 思考中…
                      </span>
                    ))}
                {m.edits?.length > 0 && !m.streaming && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {m.edits.map((idx) => (
                      <span
                        key={idx}
                        className="text-[10px] text-atelier-coral bg-atelier-coral/[0.08] border border-atelier-coral/40 px-1.5 py-0.5 rounded"
                      >
                        ✓ p{idx}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className="border-t border-atelier-line p-2.5 bg-atelier-surface2"
      >
        <div className="flex items-end gap-2">
          <textarea
            ref={inputElRef}
            data-testid="carousel-pages-chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={sending ? '生成中…' : '例：第 3 頁顏色太深、封面字再大啲…'}
            rows={1}
            disabled={sending || !pages?.length}
            className="flex-1 resize-none bg-atelier-surface border border-atelier-line focus:border-atelier-coral/60 rounded-lg px-3 py-2 text-[13px] text-atelier-ink placeholder:text-atelier-subtle outline-none max-h-32 transition-colors"
          />
          <button
            type="submit"
            data-testid="carousel-pages-chat-send"
            disabled={sending || !input.trim() || !pages?.length}
            className="flex-shrink-0 bg-atelier-coral hover:bg-atelier-coralHi disabled:bg-atelier-surface2 disabled:text-atelier-subtle text-white rounded-full w-9 h-9 flex items-center justify-center transition-all duration-300 hover:scale-105"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CarouselPagesChat;
